/**
 * People list page: contact-type filter options and URL sync (single source of truth for values + i18n keys).
 * Use with `usePeopleListFiltersFromUrl` — no scattered filter string literals in JSX.
 */

/** Query string keys for `/people` list (shareable, bookmarkable). */
export const PeoplePageSearchParam = {
  Search: "search",
  /** Contact / person type filter (maps to API `type`). */
  ContactType: "contactType",
  Labels: "labels",
  LabelLogic: "labelLogic",
  Page: "page",
  Limit: "limit",
  SortBy: "sortBy",
  SortDirection: "sortDirection",
} as const;

export const PersonContactTypeFilterId = {
  All: "all",
  Internal: "internal",
  External: "external",
  User: "user",
} as const;

export type PersonContactTypeFilterValue =
  (typeof PersonContactTypeFilterId)[keyof typeof PersonContactTypeFilterId];

/** Default matches previous Zustand store (`labelLogic: 'OR'`). */
export const DEFAULT_PEOPLE_LABEL_LOGIC = "OR" as const;

export type PeopleLabelLogic = "AND" | "OR";

export interface PeopleContactTypeFilterOption {
  value: PersonContactTypeFilterValue;
  /** i18n key under `pages` (e.g. `people.filters.internal`). */
  labelKey: string;
}

/**
 * Select options for contact type — order is display order.
 * "All" uses `common:status.all` in the filter UI (see FilterPersonTable).
 */
export const PEOPLE_CONTACT_TYPE_FILTER_OPTIONS: readonly PeopleContactTypeFilterOption[] = [
  { value: PersonContactTypeFilterId.Internal, labelKey: "people.filters.internal" },
  { value: PersonContactTypeFilterId.External, labelKey: "people.filters.external" },
  { value: PersonContactTypeFilterId.User, labelKey: "people.filters.user" },
];

const CONTACT_TYPE_VALUES = new Set<string>(Object.values(PersonContactTypeFilterId));

export function parsePersonContactTypeFilter(
  raw: string | null,
): PersonContactTypeFilterValue {
  if (raw && CONTACT_TYPE_VALUES.has(raw)) {
    return raw as PersonContactTypeFilterValue;
  }
  return PersonContactTypeFilterId.All;
}

export function parsePeopleLabelLogic(raw: string | null): PeopleLabelLogic {
  return raw === "OR" || raw === "AND" ? raw : DEFAULT_PEOPLE_LABEL_LOGIC;
}
